import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { BackButton, LoadingState } from '../components/common';
import { Heading, Text } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import Button from '../components/common/Button';
import SessionsPageShell from '../components/sessions/hub/SessionsPageShell';
import ManageSignupsModal from '../components/sessions/ManageSignupsModal';
import { useClubPermissions } from '../hooks/useClubPermissions';
import { getSessionDetails } from '../services/sessionManagementService';
import { getHostSelectCapacitySummary, isHostSelectSignup } from '../utils/signupSelection';

const SessionSignupsPage = ({ currentUser }) => {
  const { clubId, sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    club: selectedClub,
    role: clubRole,
    loading: clubLoading,
    hasPermission,
    error: clubError,
  } = useClubPermissions(clubId, currentUser);

  const [session, setSession] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [counts, setCounts] = useState(null);

  const isAdmin = currentUser?.role === 'admin' ||
    currentUser?.role === 'super_admin' ||
    clubRole === 'host' ||
    clubRole === 'admin' ||
    clubRole === 'owner';

  useEffect(() => {
    let cancelled = false;

    const loadSession = async () => {
      setLoadingSession(true);
      setSessionError(null);
      try {
        const details = await getSessionDetails(sessionId);
        if (cancelled) return;
        if (!details || details.club_id !== clubId) {
          setSession(null);
          setSessionError('Session not found for this club.');
          return;
        }
        setSession(details);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading session signups:', err);
          setSessionError(err.message || 'Failed to load session');
        }
      } finally {
        if (!cancelled) setLoadingSession(false);
      }
    };

    if (sessionId) loadSession();
    return () => {
      cancelled = true;
    };
  }, [clubId, sessionId]);

  const handleCountsChanged = useCallback((confirmed, waitlist) => {
    setCounts((current) => {
      if (current?.confirmed === confirmed && current?.waitlist === waitlist) return current;
      return { confirmed, waitlist };
    });
  }, []);

  const fromStartSession = Boolean(location.state?.fromStartSession);
  const fromBlockSetup = Boolean(location.state?.fromBlockSetup);
  const blockId = location.state?.blockId;
  const sessionsListPath = fromBlockSetup && blockId
    ? `/club/${clubId}/sessions/setup/${blockId}`
    : fromStartSession
      ? `/club/${clubId}/sessions/start`
      : `/club/${clubId}/sessions`;
  const sessionsListLabel = fromBlockSetup
    ? 'Back to block sessions'
    : fromStartSession
      ? 'Back to Start session'
      : 'Back to Plan sessions';

  const goBack = () => {
    navigate(sessionsListPath, { state: location.state });
  };

  if (!currentUser) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="text-center">
          <Heading level={2} className="mb-4">Please log in</Heading>
          <Text className="mb-6">You need to be logged in to manage signups.</Text>
          <Button onClick={() => navigate('/')} variant="primary">Go to login</Button>
        </Card>
      </SessionsPageShell>
    );
  }

  if (clubLoading || loadingSession) {
    return (
      <SessionsPageShell>
        <LoadingState message="Loading signups…" />
      </SessionsPageShell>
    );
  }

  if (clubError || !hasPermission || !isAdmin) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="py-12 text-center">
          <Heading level={2} className="mb-4">Access denied</Heading>
          <Text className="mb-6 text-text-secondary">
            {clubError || 'You need host access to manage signups.'}
          </Text>
          <Button onClick={goBack} variant="primary">{sessionsListLabel}</Button>
        </Card>
      </SessionsPageShell>
    );
  }

  if (sessionError || !session) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="py-12 text-center">
          <Heading level={2} className="mb-4">Session unavailable</Heading>
          <Text className="mb-6 text-text-secondary">{sessionError || 'This session could not be loaded.'}</Text>
          <Button onClick={goBack} variant="primary">{sessionsListLabel}</Button>
        </Card>
      </SessionsPageShell>
    );
  }

  const confirmedCount = counts?.confirmed ?? session.current_players ?? 0;
  const waitlistCount = counts?.waitlist ?? session.waitlist_players?.length ?? 0;
  const isLive = session.session_status === 'ongoing';
  const hostSelect = isHostSelectSignup(session);
  const isFull = !hostSelect && confirmedCount >= (session.max_players || 0);
  const statusLabel = isLive ? 'Active' : hostSelect ? 'Host selects' : isFull ? 'Full' : 'Open';
  const statusClass = isLive
    ? 'font-semibold text-warning-dark'
    : hostSelect
      ? 'font-semibold text-text-secondary'
      : isFull
        ? 'text-text-secondary'
        : 'font-semibold text-success-dark';

  return (
    <SessionsPageShell>
      <div>
        <BackButton className="mb-3" onClick={goBack}>
          {sessionsListLabel}
        </BackButton>
        {selectedClub?.name ? (
          <Text className="text-sm text-text-secondary">{selectedClub.name}</Text>
        ) : null}
        <div className="mt-1 gap-4 md:flex md:items-end md:justify-between">
          <Heading level={1} className="min-w-0 truncate text-xl font-bold text-text-primary sm:text-2xl">
            {session.name || 'Session'}
          </Heading>
          <p className="mt-1 text-sm text-text-secondary md:mt-0 md:shrink-0 md:text-right">
            {format(new Date(session.session_date), 'MMM d, h:mm a')}
            <span className="text-text-muted"> · </span>
            <span className="tabular-nums">
              {hostSelect
                ? getHostSelectCapacitySummary({
                    confirmedCount,
                    maxPlayers: session.max_players || 16,
                  })
                : `${confirmedCount}/${session.max_players}`}
            </span>
            {waitlistCount > 0 ? (
              <>
                <span className="text-text-muted"> · </span>
                <span className="tabular-nums">
                  {hostSelect ? `on the list ${waitlistCount}` : `waitlist ${waitlistCount}`}
                </span>
              </>
            ) : null}
            <span className="text-text-muted"> · </span>
            <span className={statusClass}>{statusLabel}</span>
          </p>
        </div>
        {isLive ? (
          <p className="mt-2 text-sm text-warning-dark">
            Session is live — change the roster in Player Management.
          </p>
        ) : null}

        <div className="mt-5">
          <ManageSignupsModal
            session={session}
            currentUser={currentUser}
            onSessionCountsChanged={handleCountsChanged}
          />
        </div>
      </div>
    </SessionsPageShell>
  );
};

export default SessionSignupsPage;
